// `logrocket` is a CommonJS `export =` module, so the type query is the SDK itself. The `.default`
// below is the interop shape of the import *expression*, which is a different thing — writing
// `typeof import('logrocket').default` here does not compile.
type LogRocketSdk = typeof import('logrocket');

let logRocketPromise: Promise<LogRocketSdk> | undefined;

/**
 * The only place `logrocket` may be imported from. Importing it is not free: the package's module
 * body calls `makeLogRocket`, which appends `<script src="https://cdn.logr-in.com/logger-1.min.js">`
 * to the head right there, and the `LogRocket` constructor publishes `window._lr_surl_cb`. Neither
 * waits for `init()`. So a static `import LogRocket from 'logrocket'` anywhere in the client bundle
 * hands LogRocket the visitor's IP, user agent and referring URL as soon as that bundle runs — no
 * matter how carefully the `init()` call below it is gated. A HAR of `/sign-in` caught exactly that:
 * the 895 KB CDN fetch landed 845 ms *before* the `shown` consent record, on a session that went on
 * to grant nothing. Deferring the import to a callsite that already checked consent is what makes
 * the gate real, so keep this module's import dynamic and keep every other module importing this
 * one instead of the package.
 *
 * The promise is cached so both callers share a single SDK instance: `identify()` before `init()`
 * is buffered on the instance and flushed when the recorder loads, which only works if it is the
 * same object.
 */
export function loadLogRocket(): Promise<LogRocketSdk> {
  logRocketPromise ??= import('logrocket').then((logRocketModule) => logRocketModule.default);

  return logRocketPromise;
}
